import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactPixel from 'react-facebook-pixel'
import moment from 'moment'

import { useAppDispatch, useAppSelector } from 'store'
import $app from 'store/app'
import $business from 'store/business'
import $customer from 'store/customer'
import $service, { ServiceType } from 'store/service'
import $resource from 'store/resource'
import $day from 'store/day'
import $order from 'store/order'

import Theme from 'utils/Theme'
import useInterval from 'hooks/useInterval'

import App from 'components/App'

import styles from './AppointmentOrderSuccess.module.scss'

export type Props = {
  onClose: () => void
}

const AppointmentOrderSuccess: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslation()

  const dispatch = useAppDispatch()
  const isRtl = useAppSelector(({ $app }) => $app.isRtl)
  const settings = useAppSelector($business.get.settings)
  const services = useAppSelector($service.get.selected)
  const resource = useAppSelector($resource.get.selected)
  const formatTime = useAppSelector($day.get.formatTime)
  const temp = useAppSelector(({ $customer }) => $customer.temp)
  const customer = useAppSelector(({ $customer }) => $customer.data)
  const bookingFieldValues = useAppSelector(({ $customer }) => $customer.bookingFieldValues)
  const payment = useAppSelector(({ $order }) => $order.payment)

  const [success, setSuccess] = useState(payment.iframe === '')
  const savedRef = useRef(false)
  const refreshedRef = useRef(false)

  useEffect(() => {
    if (success) {
      ReactPixel.track('Purchase', {
        value: payment?.data?.price || services.reduce((acc: number, service: ServiceType) => acc + Number(service.price), 0),
        currency: 'ILS',
        content_ids: services.map((service: ServiceType) => service.name),
        content_type: 'product',
        contents: services.map((service: ServiceType) => ({
          id: service.name,
          quantity: 1,
          item_price: service.price
        })),
        num_items: services.length,
        order_id: payment.orderId
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success])

  useEffect(() => {
    if (!savedRef.current && temp?.phone !== '' && temp?.name !== '') {
      savedRef.current = true
      const existingCustomerTemp = localStorage.getItem('customerTemp')
      const existingData = existingCustomerTemp ? JSON.parse(existingCustomerTemp) : {}
      
      localStorage.setItem('customerTemp', JSON.stringify({
        ...existingData,
        ...temp,
        bookingFieldValues: { ...existingData.bookingFieldValues, ...bookingFieldValues }
      }))
      dispatch($customer.set.loggedIn(true))
      dispatch($customer.set.bookingFieldValues({}))
    }
  }, [temp, bookingFieldValues, dispatch])

  useEffect(() => {
    const refreshCustomerData = async () => {
      if (success && !refreshedRef.current) {
        refreshedRef.current = true

        setTimeout(async () => {
          if (customer?.id && customer?.phone) {
            const result = await $customer.api.get({ number: customer.phone })
            if (result && result.status === 'success') {
              const { allow_book, customer: updatedCustomer, status, customers, unfilled_fields } = result
              dispatch($customer.set.data({ allow_book, customer: updatedCustomer, status, customers }))
              dispatch($customer.set.unfilled_fields(unfilled_fields))
            }
          } else if (temp?.phone) {
            const result = await $customer.api.getUnfilledFields()
            if (result && result.status === 'success') {
              dispatch($customer.set.unfilled_fields(result.unfilled_fields))
            }
          }
        }, 500)
      }
    }

    refreshCustomerData()
  }, [success, customer?.id, customer?.phone, temp?.phone, dispatch])

  const checkPayment = async () => {
    const result = await $order.api.paid({order_id: payment.orderId})
    if (result && result?.status) {
      if (result.status === 'success') {
        dispatch($order.set.payment({
          iframe: '',
          orderId: null,
          amount: 0,
          isPrepay: false,
          data: result.data ? {...result.data} : {},
        }))

        setSuccess(true)
      }

      if (result.status === 'error') {
        dispatch($order.set.payment({
          iframe: '',
          orderId: null,
          amount: 0,
          isPrepay: false,
          data: {},
        }))

        dispatch($app.set.error({
          message: t('It looks like there was an issue processing your order. Please try again. If the problem persists, contact our support team for assistance'),
          button: t('Try again'),
        }))

        if (onClose) {
          onClose()
        }
      }
    }
  }

  useInterval(checkPayment, payment.iframe !== '' ? 5000 : null)

  const handleBack = () => {
    dispatch($order.set.payment({
      iframe: '',
      orderId: null,
      amount: 0,
      isPrepay: false,
      data: {},
    }))

    dispatch($order.set.step(2))
  }

  return success ? (
    <App.Flex column aCenter gap={6} className={styles.container}>
      <App.Flex center className={styles.icon}>
        <App.Icon icon="check" xl primary />
      </App.Flex>

      <App.Text center large bold>{t('Your booking successful!')}</App.Text>

      <App.Text center muted>{t('Prepare for the best beauty service experience in you lifer. Please, let us know if you have some changes in your plans.')}</App.Text>

      <App.Flex column fullWidth gap={2} className={styles.details}>
        <App.Text bold>{services.map((service: ServiceType) => service?.name).join(', ')}</App.Text>

        <App.Flex column gap={1}>
          <App.Text xs gray>{t('Details')}:</App.Text>

          <App.Flex row wrap gap={2}>
            {settings?.display_duration ? <App.Tag icon="clock">{moment(formatTime).format('D')} {t(moment(formatTime).format('MMMM'))}, {moment(formatTime).format('HH:mm')}</App.Tag> : null}
            {resource?.id ? <App.Tag icon="user">{resource?.name}</App.Tag> : null}
            {settings?.display_price ? <App.Tag>₪{payment?.data?.price || services.reduce((acc: number, service: ServiceType) => acc + Number(service.price), 0)}</App.Tag> : null}
          </App.Flex>
        </App.Flex>
      </App.Flex>

      <App.Button fullWidth onClick={onClose}>{t('Got it!')}</App.Button>
    </App.Flex>
  ) : (
    <App.Flex column aCenter className={styles.iframeContainer}>
      <App.Flex row fullWidth className={styles.backBox}>
        <svg width="100%" height="206" viewBox="0 0 375 206" fill="none" className={styles.svg} xmlns="http://www.w3.org/2000/svg">
          <path fill={Theme.get().primary} fillOpacity="0.04" fillRule="evenodd" clipRule="evenodd" d="M260 1.17819C174.302 7.21519 89.306 22.5162 40.517 40.6892C-14.877 61.3222 -55.148 89.4622 -77.077 122.858C-83.968 133.352 -86 139.338 -86 149.144C-86 154.806 -85.468 157.334 -83.405 161.473C-76.116 176.1 -52.614 184.333 -25.922 181.609C7.442 178.205 42.761 166.455 132.5 128.904C181.017 108.602 193.678 104.458 222.097 99.5762C237.566 96.9192 272.187 95.8312 285.381 97.5872C304.407 100.118 322.333 106.453 326.898 112.257C329.639 115.742 329.553 116.763 326.25 119.881C321.659 124.215 314.353 125.732 293.5 126.681C271.636 127.677 257.182 129.616 241.519 133.654C209.921 141.801 192.099 154.662 192.022 169.371C191.97 179.412 199.737 191.089 212.551 200.233C218.648 204.584 226 207.123 226 204.878C226 204.304 223.165 201.811 219.699 199.338C212.307 194.063 206.976 188.43 205.107 183.918C202.85 178.468 204.392 174.107 210.864 167.635C220.752 157.747 239.221 150.548 267.5 145.559C277.103 143.865 286.092 143.277 312.5 142.616C330.65 142.162 348.425 141.297 352 140.692C369.725 137.696 380.47 132.344 384.589 124.459C386.405 120.983 386.404 112.341 384.587 108.828C378.785 97.6082 355.992 86.2842 327.018 80.2272C307.383 76.1232 297.415 75.3042 275.551 76.0012C247.803 76.8852 226.913 79.7162 200.492 86.1742C172.85 92.9302 157.357 99.0282 106.5 123.169C44.633 152.536 15.766 164.037 -9.033 169.202C-20.17 171.521 -37.596 171.613 -45 169.39C-53.385 166.873 -59.772 162.376 -62.033 157.396C-64.696 151.529 -64.481 148.178 -60.963 140.744C-48.17 113.699 23.676 78.3482 97.618 62.7132C140.731 53.5962 177.998 49.5602 237.506 47.5632C272.465 46.3892 332.292 47.4212 361.5 49.7012C417.396 54.0642 459.392 64.6252 478.332 79.0832C480.909 81.0502 483.47 82.6592 484.023 82.6592C485.895 82.6592 488.962 76.2172 490.684 68.6692C492.63 60.1382 492.918 43.6052 491.194 39.4312C488.89 33.8572 469.834 22.2672 453.064 16.2402C436.483 10.2812 412.024 5.26419 384 2.07319C363.995 -0.203806 287.469 -0.756806 260 1.17819Z" />
        </svg>

        <App.Flex row className={styles.back} onClick={handleBack}>
          <App.Icon icon="chevron-left" primary rotate={isRtl} />
          <App.Text small highlight>{t('Back')}</App.Text>
        </App.Flex>
      </App.Flex>

      <App.Flex row center className={styles.title}>
        {payment.isPrepay ? (
          <App.Text center height={1}>{t('Please proceed prepayment')} <App.Text inline center bold height={1}>₪{payment.amount}</App.Text></App.Text>
        ) : (
          <App.Text center height={1}>{t('Please proceed with full payment')} <App.Text inline center bold height={1}>₪{payment.amount}</App.Text></App.Text>
        )}
      </App.Flex>

      <App.Flex className={styles.iframeBox}>
        <App.Flex full center className={styles.loader}>
          <App.Loader xl />
        </App.Flex>
        <iframe src={payment.iframe} title={t('Payment iframe')} className={styles.iframe} allow="payment; clipboard-write"></iframe>
      </App.Flex>
    </App.Flex>
  )
}

export default AppointmentOrderSuccess